"use client";

import React, { useState } from "react";
import { FaqTabContent, GuideTabFaqSection } from "@/common/lib/types";
import PortableText from "@/components/common/PortableText";

interface FaqTabVariantProps {
  data: FaqTabContent;
}

export const SingleFaqSection: React.FC<{ data: GuideTabFaqSection }> = ({ data }) => {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const [allExpanded, setAllExpanded] = useState<boolean>(false);
  const faqs = data?.faqs?.filter((f) => f.enable !== false) || [];

  if (data?.enable === false || !faqs.length) return null;

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
    setAllExpanded(false);
  };

  const handleToggleAll = () => {
    if (allExpanded) {
      setAllExpanded(false);
      setOpenFaqIndex(null);
    } else {
      setAllExpanded(true);
    }
  };

  const bgColor = data.backgroundColor || "#FAFAFA";
  const underlineColor = data.underlineColor || "#D4DBDE";

  return (
    <section
      className="w-full px-4 py-8 md:py-10"
      style={{
        backgroundColor: bgColor,
      }}
    >
      <div className="container mx-auto">
        {data.title && <h2 className="text-[22px] md:text-[22px] font-semibold text-center text-black mb-3">{data.title}</h2>}
        {data.description && (
          <div className="text-center text-tertiary-6 mx-auto mb-6 text-sm md:text-base">
            <PortableText value={data.description} />
          </div>
        )}

        {(data.expandAllButton || data.collapseAllButton) && (
          <div className="flex justify-end mb-4">
            <button
              onClick={handleToggleAll}
              className="text-[13px] font-normal hover:underline cursor-pointer text-tertiary-6"
            >
              {allExpanded ? data.collapseAllButton || "Collapse All" : data.expandAllButton || "Expand All"}
            </button>
          </div>
        )}

        <div className="divide-y divide-gray-200">
          {faqs.map((faq, idx) => {
            const isOpen = allExpanded || openFaqIndex === idx;
            return (
              <div
                key={faq.key || idx}
                className="py-4"
                style={{
                  borderBottomColor: underlineColor,
                }}
              >
                <button
                  className="w-full flex items-center justify-between text-left rtl:text-right py-2 focus:outline-none cursor-pointer"
                  onClick={() => toggleFaq(idx)}
                >
                  <span className="font-semibold text-black text-[14px] md:text-[16px] pr-4 rtl:pr-0 rtl:pl-4">
                    {faq.question}
                  </span>
                  <span className="text-xl font-bold text-gray-800 transition-transform">
                    {isOpen ? "−" : "+"}
                  </span>
                </button>
                {isOpen && faq.answer && (
                  <div className="pt-3 text-[14px] text-black leading-[130%]">
                    <PortableText value={faq.answer} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const FaqTabVariant: React.FC<FaqTabVariantProps> = ({ data }) => {
  const sections = data?.sections?.filter((s) => s.enable !== false) || [];
  if (!sections.length) return null;

  return (
    <div className="w-full flex flex-col gap-[20px] md:gap-[30px] bg-white">
      {sections.map((sec, idx) => (
        <SingleFaqSection key={sec.key || idx} data={sec} />
      ))}
    </div>
  );
};

export default FaqTabVariant;
